import { CommonModule } from '@angular/common';
import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
	inject,
} from '@angular/core';
import {
	FormsModule,
	ReactiveFormsModule,
	UntypedFormBuilder,
	UntypedFormGroup,
} from '@angular/forms';
import {
	MatAutocompleteModule,
	MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterModule } from '@angular/router';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { NgxsForm } from 'src/shared/models/ngxs-form.model';
import {
	OmniSearchResult,
	TYPE_TRAVEL,
	TYPE_USER,
} from 'src/shared/models/omni-search.model';
import { AuthState } from 'src/shared/state/auth.state';
import { OmniSearch } from 'src/shared/state/main.action';
import { MainState } from 'src/shared/state/main.state';
import { TravelTypePipe } from '../pipes/travel-type.pipe';

const DEBOUNCE_TIME = 300;

@Component({
	selector: 'app-omni-search',
	templateUrl: './omni-search.component.html',
	styleUrls: ['./omni-search.component.scss'],
	imports: [
		CommonModule,
		FlexLayoutModule,
		FormsModule,
		MatAutocompleteModule,
		MatButtonModule,
		MatFormFieldModule,
		MatIconModule,
		MatInputModule,
		MatOptionModule,
		NgxsFormDirective,
		ReactiveFormsModule,
		RouterModule,
		TravelTypePipe,
	],
})
export class OmniSearchComponent implements OnInit, OnDestroy {
	private fb = inject(UntypedFormBuilder);
	private store = inject(Store);
	private router = inject(Router);

	searchElement: ElementRef;
	searchForm: UntypedFormGroup;
	omniSearchFormState: Observable<NgxsForm> = this.store.select(
		MainState.omniSearchForm
	);
	showSearch: boolean;
	searchResults: Observable<OmniSearchResult[]> = this.store.select(
		MainState.omniSearchResults
	);
	searchValueSubscription: Subscription;

	private defaultFormState = { search: '' };

	@ViewChild('searchInput')
	set searchEl(element: ElementRef) {
		this.searchElement = element;
	}

	constructor() {
		this.showSearch = false;
		this.searchForm = this.fb.group(this.defaultFormState);
	}

	ngOnInit(): void {
		this.searchValueSubscription = this.searchForm
			.get('search')
			.valueChanges.pipe(debounceTime(DEBOUNCE_TIME))
			.subscribe((searchValue) =>
				this.store.dispatch(new OmniSearch(searchValue))
			);
	}

	ngOnDestroy(): void {
		this.searchValueSubscription.unsubscribe();
	}

	openSearch(): void {
		this.showSearch = true;
		setTimeout(() => {
			this.searchElement?.nativeElement?.focus();
		}, 1);
	}

	close(): void {
		this.searchForm.reset(this.defaultFormState);
		this.showSearch = false;
	}

	optionSelected(e: MatAutocompleteSelectedEvent): void {
		const selectedResult: OmniSearchResult = e.option.value;
		this.close();
		setTimeout(() => {
			if (selectedResult.resultType === TYPE_TRAVEL) {
				this.router.navigate([
					'tacit',
					'travel',
					'itinerary',
					selectedResult.id,
				]);
			} else if (selectedResult.resultType === TYPE_USER) {
				this.router.navigate([
					'tacit',
					this.store.selectSnapshot(AuthState.canViewFullUser)
						? 'hr'
						: 'basic',
					'user',
					selectedResult.id,
				]);
			}
		}, 100);
	}
}
